/** Customer-facing ticket workflow: assigned → seen → in progress → replied. */

export const CUSTOMER_TICKET_STAGES = [
  { key: 'assigned', label: 'درخواست ثبت شد' },
  { key: 'seen', label: 'در صف بررسی' },
  { key: 'in_progress', label: 'در حال بررسی توسط کارشناس' },
  { key: 'replied', label: 'پاسخ داده شد' },
];

const STAGE_BADGE = {
  pending:  { label: 'در انتظار ارجاع', color: '#64748b', bg: '#f8fafc' },
  assigned: { label: 'ارجاع داده شده', color: '#233A7A', bg: '#EDF3FC' },
  seen:     { label: 'در انتظار پاسخ', color: '#b45309', bg: '#fffbeb' },
  in_progress: { label: 'در حال پاسخ‌گویی توسط کارشناسان مربوطه', color: '#7c3aed', bg: '#f5f3ff' },
  replied:  { label: 'پاسخ داده شده', color: '#15803d', bg: '#f0fdf4' },
};

const truthy = (v) => v === 1 || v === true || v === '1';

// True once the ticket has been referred to an actual expert/employee
// (any department other than Dispatcher), not just seen by the dispatcher.
const hasNonDispatcherReferral = (ticket) =>
  Array.isArray(ticket?.referral_roadmap) &&
  ticket.referral_roadmap.some((step) => step?.kind === 'referral' && step?.department_name && step.department_name !== 'Dispatcher');

// Each flag means "this stage has been reached" (cumulative), not "this is
// the current stage" - in_progress used to turn back off once responsed
// became true, which made CustomerTicketProgress render a completed
// ticket's 3rd step as still-pending (navy) instead of done (green).
//
// List-view endpoints (dashboard, ticket list) send a precomputed
// `referred_to_expert` boolean instead of the full referral_roadmap array
// (that's only built for the single-ticket detail fetch) - without this,
// hasNonDispatcherReferral always saw referral_roadmap as undefined on
// those pages and stage 3 could never turn green/orange there, even though
// the same ticket's detail page showed it correctly.
// The 4 raw flags come from independent signals (a dispatcher "seen" click,
// an auto-referral, a manual reply...) so a later one can legitimately be
// true while an earlier one technically never got set (e.g. auto-referred
// straight to a department without anyone clicking "seen" first). Left as
// raw booleans that produced gaps like stage 3 green with stage 2 still
// navy in the middle of it - visually nonsensical in a left-to-right
// progress bar. Stages are forced monotonic here: reaching any stage
// implies every stage before it is shown reached too.
export function getCustomerTicketStages(ticket) {
  const inProgress = ticket?.referred_to_expert != null
    ? truthy(ticket.referred_to_expert)
    : hasNonDispatcherReferral(ticket);
  const raw = [truthy(ticket?.dispatcher_seen), truthy(ticket?.refrenced_seen), inProgress, truthy(ticket?.responsed)];
  const reachedIndex = raw.reduce((furthest, v, i) => (v ? Math.max(furthest, i) : furthest), -1);
  return {
    assigned: reachedIndex >= 0,
    seen: reachedIndex >= 1,
    in_progress: reachedIndex >= 2,
    replied: reachedIndex >= 3,
  };
}

/** Highest completed stage (for a single badge on list cards). */
export function getCustomerTicketCurrentStage(ticket) {
  const { assigned, seen, in_progress: inProgress, replied } = getCustomerTicketStages(ticket);
  if (replied) return 'replied';
  if (inProgress) return 'in_progress';
  if (seen) return 'seen';
  if (assigned) return 'assigned';
  return 'pending';
}

export function getCustomerTicketStageBadge(ticket) {
  return STAGE_BADGE[getCustomerTicketCurrentStage(ticket)] || STAGE_BADGE.pending;
}

export function isCustomerRole(type) {
  return type === 'simple_user' || type === 'manager_user' || type === 'guest_user';
}

